/**
 * Regras de endereço do pagador exigidas pela API de Cobrança do Bradesco
 * (v1.6.3). O back aplica os mesmos limites; o front precisa ficar igual.
 */

const ADDRESS_MAX_LENGTH = {
  logradouro: 100,
  complemento: 30,
  bairro: 50,
  cidade: 50,
};

// Letras Latin-1 (inclusive acentuadas e ç, sem os símbolos × e ÷), números e
// espaço. Mesmo padrão do consorcio-simulador-front.
const ADDRESS_ALLOWED_CHARS_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9 ]*$/;

const ADDRESS_FORBIDDEN_CHARS_REGEX = /[^A-Za-zÀ-ÖØ-öø-ÿ0-9 ]/g;

// Hífen, meia-risca e travessão viram espaço: "Embu-Guaçu" vira "Embu Guaçu".
const ADDRESS_HYPHEN_REGEX = /[-‐‑–—]/g;

const ADDRESS_INVALID_CHARS_MESSAGE = "Use apenas letras, números e espaço.";

const ADDRESS_MAX_LENGTH_MESSAGE = "Numero máximo de caracteres excedido";

// Campos que o bot pede ao usuário quando o valor salvo está fora do padrão.
// O complemento não é enviado na proposta, por isso não tem passo próprio.
const ADDRESS_REVIEW_FIELDS = [
  { field: "logradouro", step: "informa_logradouro", required: true },
  { field: "bairro", step: "informa_bairro", required: true },
  { field: "cidade", step: "informa_cidade", required: false },
];

const toText = (value) => (value === null || value === undefined ? "" : String(value));

// NFC junta acento combinado ("a" + "̃") em um único caractere, para que
// "ã" digitado em teclados iOS/macOS seja tratado como uma letra só.
// Hífen vira espaço sem aviso; o aviso é só para caractere removido.
const sanitizeAddressInput = (value, field) => {
  const withSpaces = toText(value)
    .normalize("NFC")
    .replace(ADDRESS_HYPHEN_REGEX, " ");
  const withoutForbidden = withSpaces.replace(ADDRESS_FORBIDDEN_CHARS_REGEX, "");

  return {
    value: withoutForbidden.slice(0, ADDRESS_MAX_LENGTH[field]),
    hasForbiddenChars: withoutForbidden !== withSpaces,
  };
};

// Para valores vindos da API de CEP ou já enviados pelo usuário: troca hífen
// por espaço e tira espaços repetidos. "Embu-Guaçu" vira "Embu Guaçu". Outros
// caracteres especiais são mantidos para o usuário corrigir no passo de endereço.
const normalizeAddressValue = (value) =>
  toText(value)
    .normalize("NFC")
    .replace(ADDRESS_HYPHEN_REGEX, " ")
    .replace(/ +/g, " ")
    .trim();

const hasOnlyAllowedAddressChars = (value) =>
  ADDRESS_ALLOWED_CHARS_REGEX.test(toText(value).normalize("NFC"));

const isAddressFieldValid = (value, field) =>
  hasOnlyAllowedAddressChars(value) &&
  toText(value).length <= ADDRESS_MAX_LENGTH[field];

// Retorna o passo do bot para corrigir o primeiro campo fora do padrão, ou null.
// Com checkRequired: false, campo vazio não é considerado fora do padrão.
const getAddressReviewStep = (contrato = {}, { checkRequired = true } = {}) => {
  const invalidField = ADDRESS_REVIEW_FIELDS.find(({ field, required }) => {
    const value = contrato[field];

    if (!value) {
      return checkRequired && required;
    }

    return !isAddressFieldValid(value, field);
  });

  return invalidField ? invalidField.step : null;
};

export {
  ADDRESS_MAX_LENGTH,
  ADDRESS_ALLOWED_CHARS_REGEX,
  ADDRESS_FORBIDDEN_CHARS_REGEX,
  ADDRESS_INVALID_CHARS_MESSAGE,
  ADDRESS_MAX_LENGTH_MESSAGE,
  sanitizeAddressInput,
  normalizeAddressValue,
  hasOnlyAllowedAddressChars,
  isAddressFieldValid,
  getAddressReviewStep,
};
